import React from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

import './elastiq.css';

import MainContent from './MainContent';
import Login from './Login';
import Callback from './pages/Callback';
import SharedSessionView from './shared/SharedSessionView';
import SilentRenew from './pages/SilentRenew';
import { AgentConfigProvider } from './contexts/AgentConfigContext';
import { ThemeProvider } from './theme/ThemeProvider';
import { OAuthProvider } from './contexts/OAuthContext';
import { ToastProvider } from './components/Toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';

function AppContent() {
  const { user, loading, signOut } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/callback" element={<Callback />} />
      {/* Rendered whether or not there is a session: a public link is meant for
          someone with no account here, and the view itself decides what to ask
          for — the authenticated read when signed in, the public one otherwise.
          Gating this on `user` would have sent every public visitor to Login. */}
      <Route path="/s/:token" element={<SharedSessionView />} />
      <Route
        path="/"
        element={
          user ? (
            <MainContent
              user={user}
              setUser={() => {}}
              handleLogout={() => signOut()}
            />
          ) : (
            <Login />
          )
        }
      />
    </Routes>
  );
}

function App() {
  // /silent-renew loads in a HIDDEN IFRAME that oidc-client-ts opens for
  // background token refresh — it must do nothing but call
  // signinSilentCallback() and exit. Mounting the normal provider tree here
  // (AgentConfigProvider fetches agents, OAuthProvider fetches connections,
  // AuthProvider itself calls manager.getUser()) would fire real network
  // requests, with a possibly-unauthenticated/null user, inside that
  // invisible iframe on every single silent renewal. Short-circuit before
  // any provider mounts, rather than routing through AppContent/useAuth.
  if (window.location.pathname === '/silent-renew') {
    return <SilentRenew />;
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <AgentConfigProvider>
            <OAuthProvider>
              <Router>
                <AppContent />
              </Router>
            </OAuthProvider>
          </AgentConfigProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;